import { generateThumbnailsForUploadedFiles } from "@/components/FilesToUpload/utils";
import api from "@/services/api/axios-client";
import { retryAsync } from "@ikea-ingka-dam/retry";
import axios from "axios";
import {
  AssetsControllerCreateRequestFilesInner,
  CreateUploadDtoTypeEnum,
  StartFileuploadResult,
} from "generated";
import { useCallback, useEffect, useState } from "react";
import { extractFileExtension, getImageDimensions } from "./misc";
import { LocalFile } from "./types";
import { useCreateUploadMutation } from "./uploads";
import { useToast } from "./toast";
import { sendErrorToSentry } from "../sentry";
import {
  FILE_UPLOAD_MAX_RETRIES,
  FILE_UPLOAD_RETRY_DELAY_MS,
} from "./constants";

export const useFilesUpload = () => {
  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  const uploadMutation = useCreateUploadMutation();
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );
  const [allFilesVerified, setAllFilesVerified] = useState(false);
  const [isVerifyingFiles, setIsVerifyingFiles] = useState(false);
  const { toast } = useToast();

  const updateFileStatus = useCallback(
    (
      fileUrl: string,
      status: LocalFile["uploadStatus"],
      updates?: { fileUniqueId?: string },
    ) => {
      setLocalFiles((prev) =>
        prev.map((f) =>
          f.url === fileUrl ? { ...f, uploadStatus: status, ...updates } : f,
        ),
      );
    },
    [],
  );

  const uploadFileToStorage = async (
    localFile: LocalFile,
    uploadSession: StartFileuploadResult,
  ) => {
    await axios.put(uploadSession.uploadUrl, localFile.file, {
      headers: { "Content-Type": localFile.file.type },
      onUploadProgress: (e) => {
        setUploadProgress((prev) => ({
          ...prev,
          [localFile.url]: e.total ? Math.round((e.loaded / e.total) * 100) : 0,
        }));
      },
      withCredentials: false,
    });
  };

  const uploadSingleFile = async (
    localFile: LocalFile,
    uploadSession: StartFileuploadResult,
  ): Promise<{ fileUrl: string; fileUniqueId: string }> => {
    updateFileStatus(localFile.url, "uploading");

    await retryAsync(() => uploadFileToStorage(localFile, uploadSession), {
      retry: FILE_UPLOAD_MAX_RETRIES,
      delay: ({ index }) => FILE_UPLOAD_RETRY_DELAY_MS * Math.pow(2, index),
      error: ({ index }) => {
        if (index === 0) {
          toast(
            "We couldn't process some of your files. This action will be retried up to three times.",
          );
        }
      },
    });

    return {
      fileUrl: localFile.url,
      fileUniqueId: uploadSession.fileUniqueId,
    };
  };

  const generateUploadUrls = async (files: LocalFile[]) => {
    const { data: uploadSessions } =
      await api.assetsControllerGenerateUploadUrls({
        assetsControllerGenerateUploadUrlsRequest: {
          files: files.map(({ url, file }) => ({
            clientFileId: url,
            filename: file.name,
            filesize: file.size,
            contentType: file.type,
          })),
        },
      });
    return uploadSessions;
  };

  const uploadFilesToBucket = async (
    files: LocalFile[],
    uploadSessions: StartFileuploadResult[],
  ) => {
    const results = await Promise.allSettled(
      files.map((localFile) => {
        const uploadSession = uploadSessions.find(
          (session) => session.clientFileId === localFile.url,
        );

        if (!uploadSession) {
          return Promise.reject(new Error("No upload session found"));
        }

        return uploadSingleFile(localFile, uploadSession);
      }),
    );

    let anyFailedUploads = false;
    results.forEach((result, index) => {
      const fileUrl = files[index].url;
      if (result.status === "fulfilled") {
        updateFileStatus(fileUrl, "success", {
          fileUniqueId: result.value.fileUniqueId,
        });
      } else {
        anyFailedUploads = true;
        console.error("File upload failed", result.reason);
        sendErrorToSentry(result.reason, {
          tags: { feature: "file-upload-failed" },
          extra: { fileUrl },
        });
        updateFileStatus(fileUrl, "failed");
      }
    });

    if (anyFailedUploads) {
      toast("Something went wrong while trying to process one or more files.");
    }
  };

  const retryUpload = async (fileUrl: string) => {
    const localFile = localFiles.find((f) => f.url === fileUrl);
    if (!localFile) return;

    setAllFilesVerified(false);

    try {
      // Request a new signed URL for this single file
      const uploadSessions = await generateUploadUrls([localFile]);
      const uploadSession = uploadSessions[0];
      if (!uploadSession) {
        throw new Error("Failed to get upload URL");
      }

      updateFileStatus(fileUrl, "uploading");
      await uploadFileToStorage(localFile, uploadSession);
      updateFileStatus(fileUrl, "success", {
        fileUniqueId: uploadSession.fileUniqueId,
      });
    } catch (error) {
      console.error("Retry upload failed", error);
      sendErrorToSentry(error, {
        tags: { feature: "file-upload-retry" },
        extra: { fileUrl },
      });
      updateFileStatus(fileUrl, "failed");
      toast(
        "We are experiencing issues processing your file. Please try again later.",
      );
    }
  };

  const setOnFilesSelected = async (filesToUpload: File[]) => {
    // Process new files
    const processedFiles = await Promise.all(
      filesToUpload.map(async (file) => {
        const url = URL.createObjectURL(file);
        const baseFile: LocalFile = { file, url, uploadStatus: "pending" };

        // Only images need dimensions except image files of type tiff
        if (file.type.startsWith("image/")) {
          try {
            const dimensions = await getImageDimensions(url);
            return { ...baseFile, dimensions };
          } catch (e) {
            console.error(e);
            sendErrorToSentry(e, { tags: { feature: "file-upload" } });
          }
        }
        return baseFile;
      }),
    );

    // Append to existing files
    setLocalFiles((prev) => [...prev, ...processedFiles]);
    setAllFilesVerified(false);

    // Request signed URLs for NEW files only
    const uploadSessions = await generateUploadUrls(processedFiles);

    // Immediately start uploading the new files
    await uploadFilesToBucket(processedFiles, uploadSessions);
  };

  const createUploadAndAssets = async (uploadType: CreateUploadDtoTypeEnum) => {
    // Create the upload record
    const uploadResponse = await uploadMutation.mutateAsync({
      type: uploadType,
    });
    const createdUpload = uploadResponse.data;

    const { assetFiles, thumbnailData } = localFiles.reduce<{
      assetFiles: Array<AssetsControllerCreateRequestFilesInner>;
      thumbnailData: StartFileuploadResult[];
    }>(
      (acc, f) => {
        if (f.uploadStatus === "success" && f.fileUniqueId) {
          acc.assetFiles.push({
            uploadId: createdUpload.id,
            fileUniqueId: f.fileUniqueId,
            filename: f.file.name,
            filesize: f.file.size,
            fileExtension: extractFileExtension(f.file.name),
          });
          acc.thumbnailData.push({
            clientFileId: f.url,
            filename: f.file.name,
            filesize: f.file.size,
            contentType: f.file.type,
            fileUniqueId: f.fileUniqueId,
            uploadUrl: "",
          });
        }
        return acc;
      },
      { assetFiles: [], thumbnailData: [] },
    );

    // Create the assets using the stored fileUniqueIds
    await api.assetsControllerCreate({
      assetsControllerCreateRequest: {
        files: assetFiles,
      },
    });

    // Generate and upload thumbnails for uploaded files
    const thumbnails = await generateThumbnailsForUploadedFiles(
      thumbnailData,
      localFiles,
    );

    if (thumbnails.length > 0) {
      await api.assetsControllerCreateThumbnails({
        uploadId: createdUpload.id,
        files: thumbnails,
      });
    }

    return { createdUpload };
  };

  const removeFile = async (fileUrl: string) => {
    setLocalFiles((prev) =>
      prev.filter((localFile) => localFile.url !== fileUrl),
    );
    URL.revokeObjectURL(fileUrl);
    // Reset verification when files change
    setAllFilesVerified(false);
  };

  const allFilesUploaded =
    localFiles.length > 0 &&
    localFiles.every((f) => f.uploadStatus === "success");

  const anyUploading = localFiles.some(
    (f) => f.uploadStatus === "uploading" || f.uploadStatus === "pending",
  );

  // Verify files exist in bucket when all uploads are successful
  useEffect(() => {
    if (!allFilesUploaded || allFilesVerified) return;
    setIsVerifyingFiles(true);
    const verifyFiles = async () => {
      const fileUniqueIds = localFiles
        .filter((f) => f.fileUniqueId)
        .map((f) => f.fileUniqueId!);

      if (fileUniqueIds.length === 0) {
        setAllFilesVerified(true);
        return;
      }

      try {
        const { data: verificationResult } =
          await api.assetsControllerVerifyFilesExist({
            verifyFilesExistDto: { fileUniqueIds },
          });

        if (verificationResult.allExist) {
          setAllFilesVerified(true);
          setIsVerifyingFiles(false);
        } else {
          // Mark missing files as failed so user can manually retry
          setLocalFiles((prev) =>
            prev.map((file) =>
              file.fileUniqueId &&
              verificationResult.missingFileIds.includes(file.fileUniqueId)
                ? {
                    ...file,
                    uploadStatus: "failed",
                  }
                : file,
            ),
          );
          setIsVerifyingFiles(false);
        }
      } catch (error) {
        setIsVerifyingFiles(false);
        console.error("Verification failed:", error);
        sendErrorToSentry(error, {
          tags: { feature: "file-upload-verification" },
        });
        toast("Something went wrong, please try again later.");
      }
    };

    verifyFiles();
  }, [allFilesUploaded, allFilesVerified, localFiles, toast]);

  const canCreateUpload = allFilesUploaded && allFilesVerified;

  return {
    localFiles,
    setOnFilesSelected,
    uploadProgress,
    removeFile,
    createUploadAndAssets,
    allFilesUploaded,
    anyUploading,
    retryUpload,
    canCreateUpload,
    isVerifyingFiles,
  };
};
